import {
  AdditionalInterestType,
  Skp,
  SkpAdditionalInterest,
  SkpClass,
} from "api/quote";
import {Box} from "components/Box";
import {GridContainer} from "components/Grid";
import FormTitle from "../../../components/FormTitle";
import Field from "../../Field";
import ActionButton from "../../ActionButton";
import {css} from "@emotion/react";
import styled from "@emotion/styled";
import {PlacesAutocomplete} from "../../PlacesAutoComplete";
import {aggOccurLimits} from "../shared/HabitationalGeneralLiability";
import {sortedStates} from "../../fieldValues";
import SkpClassForm from "./SkpClassForm";

const SiteAddress = styled.div`
  padding-left: 10%;
  padding-top: 1%;
  padding-bottom: 1%;
`;
const additionalInterestTypes = [
  {
    label: "Controlling Interest",
    value: AdditionalInterestType.CONTROLLING_INTERESTS,
  },
  {
    label: "Designated Person or Organization",
    value: AdditionalInterestType.DESIGNATED_PERSON_OR_ORGANIZATION,
  },
  {
    label: "Lessor of Leased Equipment",
    value: AdditionalInterestType.LESSOR_OF_LEASED_EQUIPMENT,
  },
  {
    label: "Managers or Lessors of Premises",
    value: AdditionalInterestType.MANAGERS_OR_LESSORS_OF_PREMISES,
  },
  {
    label: "Mortgagee, Assignee, or Receiver",
    value: AdditionalInterestType.MORTGAGEE_ASSIGNEE_OR_RECEIVER,
  },
  {
    label: "Owners, Lessees or Contractors - Scheduled",
    value: AdditionalInterestType.OWNERS_LESSEES_CONTRACTORS_SCHEDULED,
  },
  {
    label: "State or Governmental Agency",
    value: AdditionalInterestType.STATE_OR_GOVERNMENTAL_AGENCY,
  },
];

function AdditionalInterestsForm({
  skpAdditionalInterests,
  quoteId,
}: {
  skpAdditionalInterests: SkpAdditionalInterest[];
  quoteId: string;
}) {
  return (
    <GridContainer rowGap={24} columnGap={16}>
      {skpAdditionalInterests.map((skp_additional_interest, index) => {
        if (
          skp_additional_interest.additional_interest_type !==
            AdditionalInterestType.OWNERS_LESSEES_CONTRACTORS_AUTOMATIC &&
          skp_additional_interest.additional_interest_type !==
            AdditionalInterestType.COMPLETED_OPERATIONS
        )
          return (
            <Box width="100%" borderBottom="2px solid lightBlue" my={20}>
              <Box width="100%" display="flex">
                <FormTitle.SubHeader>
                  Additional Interest {index + 1}
                </FormTitle.SubHeader>
                <Box width="1%"></Box>
                <Field.DropDown
                  object={skp_additional_interest}
                  options={additionalInterestTypes.map((item) => ({
                    title: item.label,
                    value: item.value,
                  }))}
                  field={"additional_interest_type"}
                  label="Select Additional Interest"
                  wrapperProps={{
                    grow: 12,
                  }}
                />
                <Box width="5%"></Box>
                <Box
                  css={css`
                    margin-bottom: 10px;
                  `}
                >
                  <ActionButton.Delete
                    objectName="skp_additional_interest"
                    objectId={skp_additional_interest.id}
                  >
                    Delete AI {index + 1}
                  </ActionButton.Delete>
                </Box>
              </Box>
              <SiteAddress>
                <FormTitle.SubHeader>Address</FormTitle.SubHeader>
                <Box width="83%">
                  <PlacesAutocomplete
                    object={skp_additional_interest}
                    quoteId={quoteId}
                  />
                </Box>
                <GridContainer rowGap={24} columnGap={16}>
                  <Field.TextInput
                    object={skp_additional_interest}
                    field={"name"}
                    label="Name"
                    wrapperProps={{
                      grow: 5,
                    }}
                  />
                  <Field.TextInput
                    object={skp_additional_interest}
                    field={"line_1"}
                    label="Address"
                    wrapperProps={{
                      grow: 5,
                    }}
                  />
                  <Field.TextInput
                    object={skp_additional_interest}
                    field={"city"}
                    label="City"
                    wrapperProps={{
                      growXXS: 12,
                      growMD: 3,
                    }}
                  />
                  <Field.DropDown
                    object={skp_additional_interest}
                    options={sortedStates.map((state) => ({
                      title: state.name,
                      value: state.abbreviation,
                    }))}
                    field={"state"}
                    label="Select State"
                    wrapperProps={{
                      growXXS: 12,
                      growMD: 3,
                    }}
                  />
                  <Field.TextInput
                    object={skp_additional_interest}
                    field={"zip"}
                    label="Zip"
                    wrapperProps={{
                      growXXS: 12,
                      growMD: 4,
                    }}
                  />
                </GridContainer>
              </SiteAddress>
              {skp_additional_interest.site_address ? (
                <SiteAddress>
                  <FormTitle.SubHeader>Site Address</FormTitle.SubHeader>
                  <GridContainer rowGap={24} columnGap={16}>
                    <Box width="83%">
                      <PlacesAutocomplete
                        object={skp_additional_interest.site_address}
                        quoteId={quoteId}
                      />
                    </Box>
                    <Field.TextInput
                      object={skp_additional_interest.site_address}
                      field={"name"}
                      label="Name"
                      wrapperProps={{
                        grow: 5,
                      }}
                    />
                    <Field.TextInput
                      object={skp_additional_interest.site_address}
                      field={"line_1"}
                      label="Address"
                      wrapperProps={{
                        grow: 5,
                      }}
                    />
                    <Field.TextInput
                      object={skp_additional_interest.site_address}
                      field={"city"}
                      label="City"
                      wrapperProps={{
                        growXXS: 12,
                        growMD: 3,
                      }}
                    />
                    <Field.DropDown
                      object={skp_additional_interest.site_address}
                      options={sortedStates.map((state) => ({
                        title: state.name,
                        value: state.abbreviation,
                      }))}
                      field={"state"}
                      label="Select State"
                      wrapperProps={{
                        growXXS: 12,
                        growMD: 3,
                      }}
                    />
                    <Field.TextInput
                      object={skp_additional_interest.site_address}
                      field={"zip"}
                      label="Zip"
                      wrapperProps={{
                        growXXS: 12,
                        growMD: 4,
                      }}
                    />
                  </GridContainer>
                </SiteAddress>
              ) : (
                <></>
              )}
            </Box>
          );
        return <></>;
      })}

      <Box width="100%" my={20} display="flex" justifyContent="center">
        <Box width="75%"></Box>
        <ActionButton.Add objectName="skp_additional_interest" background>
          Other additional interest options
        </ActionButton.Add>
      </Box>
    </GridContainer>
  );
}
function ClassesForm({skpClasses}: {skpClasses: SkpClass[]}) {
  const numSuffix: {[key: number]: string} = {
    2: "nd",
    3: "rd",
  };

  return (
    <Box mb={100}>
      {skpClasses.map((skp_class, index) => (
        <Box
          key={index}
          mb={50}
          borderBottom={skpClasses.length > 1 ? "1px solid #ccc" : undefined}
        >
          <FormTitle.SubHeader>
            {!index ? "Primary" : index + 1 + numSuffix[index + 1]} Class
          </FormTitle.SubHeader>
          {skpClasses.length > 1 ? (
            <Box display="flex" justifyContent="space-between" my={25}>
              <Box></Box>
              {!skp_class.primary_class ? (
                <ActionButton.Delete
                  objectName="skp_class"
                  objectId={skp_class.id}
                >
                  Delete {index + 1 + numSuffix[index + 1]} Class
                </ActionButton.Delete>
              ) : (
                <></>
              )}
            </Box>
          ) : (
            <></>
          )}
          <SkpClassForm skp_class={skp_class} />
          <Box my={10}></Box>
        </Box>
      ))}

      <Box my={8}>
        {skpClasses.length < 3 ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box></Box>
            <ActionButton.Add objectName="skp_class" background>
              Add Class
            </ActionButton.Add>
          </Box>
        ) : (
          <></>
        )}
      </Box>
    </Box>
  );
}

function SkpGeneralLiability({skp, quoteId}: {skp: Skp; quoteId: string}) {
  return (
    <Box mb={100}>
      <GridContainer rowGap={24} columnGap={16}>
        <Field.DropDown
          options={aggOccurLimits.map((item) => ({
            title: item.label,
            value: item.value,
          }))}
          object={skp}
          field="agg_occur_limit"
          label="Occurence Limit / Aggregate Limit"
          wrapperProps={{
            grow: 12,
          }}
        />
        <ClassesForm skpClasses={skp.skp_classes} />
      </GridContainer>
      <GridContainer rowGap={24} columnGap={16}>
        <FormTitle.SubHeader>Additional Interests</FormTitle.SubHeader>
        <Field.CheckBox
          label="Blanket Insured"
          object={skp}
          field={"additional_insured_blanket_insured"}
          wrapperProps={{
            grow: 12,
          }}
        />
        <Field.CheckBox
          label="Terrorism Coverage"
          object={skp}
          field={"terrorism_coverage_flag"}
          wrapperProps={{
            grow: 12,
          }}
        />
        {skp.additional_insured_blanket_insured && (
          <IndentPersonalProperty>
            <Field.CheckBox
              label="Completed Operations"
              object={skp}
              field={"completed_operations"}
              wrapperProps={{
                grow: 12,
              }}
            />
          </IndentPersonalProperty>
        )}
        <Box
          my={50}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box></Box>
          <AdditionalInterestsForm
            skpAdditionalInterests={skp.skp_additional_interests}
            quoteId={quoteId}
          />
        </Box>
      </GridContainer>
    </Box>
  );
}

const IndentPersonalProperty = styled.div`
  padding-left: 2%;
  margin-left: 1%;
`;

export default SkpGeneralLiability;
